import { Upload, CheckCircle, Clock, ExternalLink, RefreshCw } from 'lucide-react';
import { ProjectData } from '../types';
import { resolveSupportContact, supportPhoneTelHref } from '../utils/supportContact';

interface AppStoreSubmissionProps {
  project: ProjectData;
}

export const AppStoreSubmission = ({ project }: AppStoreSubmissionProps) => {
  const { appType, jiraKey } = project;
  const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
  const support = resolveSupportContact(appType, project.supportContact);

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Upload className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              🚀 App Store Submission In Progress
            </h3>
            <p className="text-green-700 mb-4">
              Great news! We are now actively preparing and submitting your {appName} app to the Apple App Store and Google Play Store. Our team is working on the final submission process and will keep you updated on progress.
            </p>
            <div className="bg-white border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800 font-medium mb-2">🎉 Milestone Reached</p>
              <p className="text-sm text-green-700">
                Your app has reached the App Store submission phase - this is an exciting milestone! We're preparing it for both Apple App Store and Google Play Store submission.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Tracking */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📋 Submission Progress
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center">
              <CheckCircle className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">App Build Preparation</h3>
              <p className="text-gray-600 text-sm">Ensuring your app meets all Apple App Store and Google Play Store requirements and guidelines</p>
            </div>
            <span className="text-green-600 text-sm font-medium">Complete</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              <RefreshCw className="h-4 w-4 animate-spin" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">App Store Metadata Submission</h3>
              <p className="text-gray-600 text-sm">Uploading descriptions, screenshots, and app information to Apple App Store Connect and Google Play Console</p>
            </div>
            <span className="text-blue-600 text-sm font-medium">In Progress</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-gray-100 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Review Process Initiation</h3>
              <p className="text-gray-600 text-sm">Submitting your app for Apple's and Google's review processes</p>
            </div>
            <span className="text-gray-500 text-sm font-medium">Pending</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-gray-100 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Store Review & Approval</h3>
              <p className="text-gray-600 text-sm">Apple and Google review your app for compliance and approval</p>
            </div>
            <span className="text-gray-500 text-sm font-medium">Pending</span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">
          ⏱️ Expected Timeline
        </h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              Now
            </div>
            <div>
              <p className="font-medium text-blue-900">App submission preparation and upload</p>
              <p className="text-blue-700 text-sm">We're actively working on this now</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              1-2
            </div>
            <div>
              <p className="font-medium text-blue-900">24-48 hours: Apple and Google begin review process</p>
              <p className="text-blue-700 text-sm">Both stores will start their review once submitted</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              1-7
            </div>
            <div>
              <p className="font-medium text-blue-900">1-7 days: Store review completion</p>
              <p className="text-blue-700 text-sm">Typical timeframe for Apple (1-7 days) and Google Play (3-7 days) review processes</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              ✓
            </div>
            <div>
              <p className="font-medium text-blue-900">Upon approval: App goes live in the stores</p>
              <p className="text-blue-700 text-sm">Your app will be available for download in Apple App Store and Google Play Store</p>
            </div>
          </div>
        </div>
      </div>

      {/* What We're Handling */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          🔧 What We're Handling For You
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Build Optimization</p>
                <p className="text-gray-600 text-sm">Ensuring your app meets Apple App Store and Google Play Store technical requirements</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Metadata Preparation</p>
                <p className="text-gray-600 text-sm">App descriptions, keywords, and store listings</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Screenshot Upload</p>
                <p className="text-gray-600 text-sm">Optimizing and uploading app screenshots</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Compliance Review</p>
                <p className="text-gray-600 text-sm">Verifying all Apple App Store and Google Play Store guidelines are met</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Submission Process</p>
                <p className="text-gray-600 text-sm">Handling the technical submission to App Store Connect and Google Play Console</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ✓
              </div>
              <div>
                <p className="font-medium text-gray-900">Progress Tracking</p>
                <p className="text-gray-600 text-sm">Monitoring the review process and updates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help Resources */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📚 Help Resources
        </h3>
        <div className="space-y-3">
          <a
            href={`/project/${jiraKey}/help#app-store-submission`}
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              📖
            </div>
            <div>
              <p className="font-medium text-gray-900">App Store Submission Guide</p>
              <p className="text-sm text-gray-600">Learn more about the submission process and timeline for both Apple App Store and Google Play Store</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="https://developer.apple.com/app-store/review/guidelines/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              📋
            </div>
            <div>
              <p className="font-medium text-gray-900">Apple App Store Review Guidelines</p>
              <p className="text-sm text-gray-600">Official Apple guidelines for app approval</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="https://play.google.com/about/developer-content-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center">
              📋
            </div>
            <div>
              <p className="font-medium text-gray-900">Google Play Store Content Policy</p>
              <p className="text-sm text-gray-600">Official Google Play policies for app approval</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
        </div>
      </div>

      {/* Support Contact */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-indigo-900 mb-3">
          🆘 Questions About the Submission Process?
        </h3>
          <p className="text-indigo-800 mb-4">
          Our support team is here to help throughout the App Store submission process for both Apple App Store and Google Play Store:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-100 text-indigo-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-indigo-900">Email Support</p>
              <p className="text-sm text-indigo-700">
                <a href={`mailto:${support.email}`} className="hover:text-indigo-900">
                  {support.email}
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-100 text-indigo-800 rounded-full w-8 h-8 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-indigo-900">Phone Support</p>
              <p className="text-sm text-indigo-700">
                <a href={supportPhoneTelHref(support.phone)} className="hover:text-indigo-900">
                  {support.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          🎯 What Happens Next?
        </h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              1
            </div>
            <div>
              <p className="font-medium text-gray-900">We complete the submission</p>
              <p className="text-gray-600 text-sm">Our team finishes preparing and submits your app to Apple App Store and Google Play Store</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              2
            </div>
            <div>
              <p className="font-medium text-gray-900">Stores review your app</p>
              <p className="text-gray-600 text-sm">Apple's and Google's review teams examine your app for compliance and quality</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              3
            </div>
            <div>
              <p className="font-medium text-gray-900">You get notified of approval</p>
              <p className="text-gray-600 text-sm">We'll notify you immediately when your app is approved and live</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              4
            </div>
            <div>
              <p className="font-medium text-gray-900">Your app goes live!</p>
              <p className="text-gray-600 text-sm">Your {appName} app will be available for download in the Apple App Store and Google Play Store</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
